import React, { useEffect, useMemo, useState } from "react";
import Pagination from "../../../../components/ui/Pagination";
import {
  ROW_STATUS,
  groupResultsByRow,
  countByStatus,
  buildErrorCsv,
  hasProblems,
  downloadTextFile,
} from "./importHelpers";

const STATUS_LABEL = {
  [ROW_STATUS.SUCCESS]: "Success",
  [ROW_STATUS.PARTIAL]: "Partial",
  [ROW_STATUS.FAILED]: "Failed",
};
const ITEM_STATUS_CLASS = {
  SUCCESS: "import-status--success",
  "PARTIAL SUCCESS": "import-status--partial",
  FAILED: "import-status--failed",
};
const ROW_STATUS_CLASS = {
  [ROW_STATUS.SUCCESS]: "import-status--success",
  [ROW_STATUS.PARTIAL]: "import-status--partial",
  [ROW_STATUS.FAILED]: "import-status--failed",
};

// Stable fallback so grouping (can be 8,000 rows) only reruns when results change
const NO_RESULTS = [];

const SummaryCard = ({ label, value, tone }) => (
  <div
    className={`import-summary-card ${tone ? `import-summary-card--${tone}` : ""}`}
  >
    <div className="label">{label}</div>
    <div className="value">{(value || 0).toLocaleString()}</div>
  </div>
);

/** State C: finished (COMPLETED or FAILED) — summary, grouped results, CSV export. */
const ImportResults = ({ batch, onImportAnother }) => {
  const stats = batch?.stats || {};
  const results = batch?.results || NO_RESULTS;
  const groups = useMemo(() => groupResultsByRow(results), [results]);
  const counts = useMemo(() => countByStatus(groups), [groups]);

  // Default to the Failed tab when there are failures
  const [tab, setTab] = useState(() =>
    counts[ROW_STATUS.FAILED] > 0 ? ROW_STATUS.FAILED : "ALL",
  );
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [expanded, setExpanded] = useState(() => new Set());

  useEffect(() => setPage(1), [tab, search, limit]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return groups.filter(
      (g) =>
        (tab === "ALL" || g.status === tab) &&
        (!q ||
          g.name.toLowerCase().includes(q) ||
          g.employeeId.toLowerCase().includes(q)),
    );
  }, [groups, tab, search]);

  const pageRows = filtered.slice((page - 1) * limit, page * limit);

  const toggle = (row) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(row) ? next.delete(row) : next.add(row);
      return next;
    });

  const downloadErrors = () =>
    downloadTextFile(
      buildErrorCsv(results),
      `import_errors_${(batch?.batchId || "batch").slice(0, 8)}.csv`,
    );

  const tabs = [
    { key: "ALL", label: "All", count: groups.length },
    {
      key: ROW_STATUS.FAILED,
      label: "Failed",
      count: counts[ROW_STATUS.FAILED],
    },
    {
      key: ROW_STATUS.PARTIAL,
      label: "Partial",
      count: counts[ROW_STATUS.PARTIAL],
    },
    {
      key: ROW_STATUS.SUCCESS,
      label: "Success",
      count: counts[ROW_STATUS.SUCCESS],
    },
  ];

  return (
    <div className="d-flex flex-column gap-3">
      {batch?.status === "FAILED" && (
        <div
          className="alert alert-danger d-flex align-items-start gap-2 m-0"
          role="alert"
        >
          <i className="bi bi-exclamation-octagon-fill mt-1"></i>
          <div>
            <strong>The import stopped unexpectedly.</strong> Rows processed
            before the error were saved.
          </div>
        </div>
      )}

      <div className="import-card">
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
          <div>
            <h5 className="fw-bold text-navy mb-1">
              {batch?.status === "FAILED"
                ? "Import stopped"
                : "Import finished"}
            </h5>
            <p className="text-slate-500 fs-085 mb-0">
              <i className="bi bi-file-earmark-excel me-1"></i>
              {batch?.fileName || "Uploaded file"}
            </p>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <button
              type="button"
              className="btn btn-outline-danger profile-btn"
              onClick={downloadErrors}
              disabled={!hasProblems(results)}
              title={
                hasProblems(results)
                  ? "Failed and partial items as CSV"
                  : "No errors to download"
              }
            >
              <i className="bi bi-download"></i> Download errors (CSV)
            </button>
            <button
              type="button"
              className="btn btn-dark profile-btn"
              onClick={onImportAnother}
            >
              <i className="bi bi-arrow-repeat"></i> Import another file
            </button>
          </div>
        </div>

        <div className="import-summary">
          <SummaryCard label="Total" value={stats.total} />
          <SummaryCard label="Success" value={stats.success} tone="success" />
          <SummaryCard label="Partial" value={stats.partial} tone="partial" />
          <SummaryCard label="Failed" value={stats.failed} tone="failed" />
        </div>
      </div>

      <div className="glass-table-card">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 p-3">
          <div
            className="approval-subtabs"
            role="tablist"
            aria-label="Filter results"
          >
            {tabs.map((t) => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={tab === t.key}
                className={`approval-subtab ${tab === t.key ? "active" : ""}`}
                onClick={() => setTab(t.key)}
              >
                {t.label}{" "}
                <span className="opacity-80">({t.count.toLocaleString()})</span>
              </button>
            ))}
          </div>
          <div className="position-relative import-search">
            <i className="bi bi-search position-absolute top-50 translate-middle-y ms-3 text-slate-500 fs-080"></i>
            <input
              type="search"
              className="filter-input w-100 ps-5"
              placeholder="Search name or employee ID"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search results"
            />
          </div>
        </div>

        <div className="table-responsive">
          <table className="table table-hover mb-0 import-table">
            <thead className="custom-table-header">
              <tr>
                <th className="ps-4 py-3 col-w-80">Row #</th>
                <th className="py-3">Employee</th>
                <th className="py-3">Status</th>
                <th className="py-3">Records</th>
                <th className="py-3 pe-4 text-end">Details</th>
              </tr>
            </thead>
            <tbody className="border-top-0">
              {pageRows.map((g) => {
                const isOpen = expanded.has(g.row);
                const problemCount = g.items.filter(
                  (i) => i.status !== "SUCCESS",
                ).length;
                return (
                  <React.Fragment key={g.row}>
                    <tr
                      className="cursor-pointer"
                      onClick={() => toggle(g.row)}
                    >
                      <td className="ps-4 fw-semibold">{g.row}</td>
                      <td>
                        <div className="emp-name-text">{g.name}</div>
                        <div className="emp-sub-text">
                          {g.employeeId || "No employee ID"}
                        </div>
                      </td>
                      <td>
                        <span
                          className={`import-status ${ROW_STATUS_CLASS[g.status]}`}
                        >
                          {STATUS_LABEL[g.status]}
                        </span>
                      </td>
                      <td className="fs-080 text-slate-600">
                        {g.items.length} table{g.items.length === 1 ? "" : "s"}
                        {problemCount > 0 && (
                          <span className="text-danger ms-1">
                            · {problemCount} issue
                            {problemCount === 1 ? "" : "s"}
                          </span>
                        )}
                      </td>
                      <td className="pe-4 text-end">
                        <button
                          type="button"
                          className="btn btn-sm btn-link text-slate-600 p-0"
                          aria-expanded={isOpen}
                          aria-label={isOpen ? "Hide details" : "Show details"}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggle(g.row);
                          }}
                        >
                          <i
                            className={`bi ${isOpen ? "bi-chevron-up" : "bi-chevron-down"}`}
                          ></i>
                        </button>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="import-detail-row">
                        <td></td>
                        <td colSpan={4} className="pe-4">
                          <ul className="import-detail-list">
                            {g.items.map((item, i) => (
                              <li key={`${item.table}-${i}`}>
                                <span
                                  className={`import-status import-status--sm ${ITEM_STATUS_CLASS[item.status] || ""}`}
                                >
                                  {item.status}
                                </span>
                                <span className="table-name">{item.table}</span>
                                <span className="message">{item.message}</span>
                              </li>
                            ))}
                          </ul>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}

              {pageRows.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center py-5 text-slate-500">
                    {groups.length === 0
                      ? "No results were returned for this import."
                      : "No rows match this filter."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          page={page}
          limit={limit}
          total={filtered.length}
          onPageChange={setPage}
          onLimitChange={setLimit}
        />
      </div>
    </div>
  );
};

export default ImportResults;
